import { useMemo } from 'react'
import { ArrowUpRight, TrendingUp } from 'lucide-react'
import { fmtMoneda } from '@/lib/cd2bPricing'

/**
 * ComparadorEdiciones — herramienta de UPSELL para el asesor. Dada la edición actual y el catálogo,
 * muestra qué GANA el cliente si sube a una edición superior: cuánto más cuesta la base y qué módulos
 * pasarían a estar INCLUIDOS (con énfasis en los que hoy ya paga como add-on → "subir se los regala").
 *
 * Puro/derivado del catálogo enriquecido (`getCatalogo`): ediciones con `precioBase`/`modulosIncluidos`,
 * módulos con `precio`. `onSubir(edicion)` cambia la edición en el editor (mismo handler que los botones).
 */
export default function ComparadorEdiciones({ catalogo, edicionActual, modulosSel = [], moneda = 'USD', onSubir }) {
  const ediciones = useMemo(() => catalogo?.ediciones || [], [catalogo?.ediciones])
  const modulos = useMemo(() => catalogo?.modulos || [], [catalogo?.modulos])
  const labelMod = (code) => modulos.find((m) => m.code === code)?.label || code

  const actual = ediciones.find((e) => e.edicion === edicionActual)
  const selSet = useMemo(() => new Set(modulosSel), [modulosSel])

  const upgrades = useMemo(() => {
    if (!actual) return []
    const incActual = new Set(actual.modulosIncluidos || [])
    const precioMod = (code) => Number(modulos.find((m) => m.code === code)?.precio) || 0
    return ediciones
      .filter((e) => e.edicion !== edicionActual)
      .map((e) => {
        const ganados = (e.modulosIncluidos || []).filter((c) => !incActual.has(c))
        const deltaBase = (e.precioBase != null && actual.precioBase != null) ? e.precioBase - actual.precioBase : null
        // Módulos que HOY están vendidos como add-on (extra) y que esta edición incluiría gratis.
        const yaPagados = ganados.filter((c) => selSet.has(c) && !incActual.has(c))
        const ahorro = yaPagados.reduce((s, c) => s + precioMod(c), 0)
        return { ...e, ganados, deltaBase, yaPagados, ahorro }
      })
      .filter((e) => e.ganados.length > 0)
      // Ordena: primero las que dan más ahorro inmediato, luego por menor delta.
      .sort((a, b) => (b.ahorro - a.ahorro) || ((a.deltaBase ?? 1e9) - (b.deltaBase ?? 1e9)))
      .slice(0, 3)
  }, [actual, ediciones, modulos, edicionActual, selSet])

  if (!actual || upgrades.length === 0) return null

  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <p className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200 flex items-center gap-1.5 mb-3">
        <TrendingUp size={15} className="text-primary-600 dark:text-primary-300" /> Oportunidades de upsell
      </p>
      <div className="space-y-2.5">
        {upgrades.map((e) => (
          <div key={e.edicion} className="rounded-md border border-line bg-surface-muted p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-body-sm font-medium text-neutral-800 dark:text-neutral-200">
                  Subir a {e.label}
                  {e.deltaBase != null && (
                    <span className="text-caption text-neutral-500 dark:text-neutral-400 font-normal">
                      {' '}· {e.deltaBase > 0 ? `+${fmtMoneda(e.deltaBase, moneda)}/mes` : 'sin costo extra'}
                    </span>
                  )}
                </p>
                <p className="text-caption text-neutral-600 dark:text-neutral-400 mt-0.5">
                  Incluye {e.ganados.length} módulo{e.ganados.length === 1 ? '' : 's'} más:{' '}
                  <span className="text-neutral-500 dark:text-neutral-400">{e.ganados.slice(0, 5).map(labelMod).join(', ')}{e.ganados.length > 5 ? '…' : ''}</span>
                </p>
                {e.ahorro > 0 && (
                  <p className="text-caption text-success-700 dark:text-success-300 mt-1">
                    Ya paga {fmtMoneda(e.ahorro, moneda)}/mes en add-ons que esta edición incluiría gratis.
                  </p>
                )}
              </div>
              {onSubir && (
                <button type="button" onClick={() => onSubir(e.edicion)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/40 px-2.5 py-1.5 text-caption font-medium">
                  Aplicar <ArrowUpRight size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
